import { callPublicEdgeFunction } from '@/lib/edge';
import type { Member } from '@/lib/db/types';
import { getInviteTokenFromUrl, getStoredInviteToken, isSignupEntryPath } from '@/lib/invite-token';

type InvitationStatus = 'pending' | 'accepted' | 'expired' | 'cancelled' | string;

/**
 * True when the user should see onboarding (fresh or in-progress invite).
 * Returning members with stale sessionStorage tokens are sent to sign-in instead.
 */
export async function shouldRouteToOnboarding(token: string): Promise<boolean> {
  const freshFromUrl = getInviteTokenFromUrl() === token;

  try {
    const inv = await callPublicEdgeFunction<{
      status: InvitationStatus;
    }>('validate-invitation', { token });
    return inv.status === 'pending';
  } catch {
    // Magic-link return has token in storage, not the URL — stay in onboarding if validate is unreachable.
    return freshFromUrl || token === getStoredInviteToken();
  }
}

/**
 * Auth session exists but no members row yet — finish onboarding before dashboard.
 */
export function isMidInviteOnboarding(hasSession: boolean, hasMember: boolean, token: string | null): boolean {
  if (!hasSession || hasMember) return false;
  if (token) return true;
  return isSignupEntryPath() && Boolean(getStoredInviteToken());
}

/**
 * Only enter the main app when the member is fully onboarded and no pending invite remains.
 */
export async function shouldEnterMainApp(member: Member | null, token: string | null): Promise<boolean> {
  if (!member) return false;
  if (!member.onboarded_at) return false;
  if (!token) return true;
  return !(await shouldRouteToOnboarding(token));
}
